import { useCallback, useRef, useState } from 'react'

import {
  AdaptedProduct,
  CatalogBlock,
  Context,
  UpdateBlockParams,
} from '@/types'
import { sanitizeCatalogBlocks } from '@/utils'

type CatalogStateCallbacks = {
  onBlockChange?: (
    blockId: string,
    block: CatalogBlock,
    updatedBlock: CatalogBlock,
  ) => void
  onBlocksReorder?: (blocks: CatalogBlock[]) => void
}

type ReconcileBlocksOptions = {
  preserveBlockIds?: Set<string>
}

export const useCatalogState = (
  callbacks?: CatalogStateCallbacks,
  context?: Context, // Add context for sanitization
) => {
  // Use a Map for O(1) lookups and updates
  const blocksMap = useRef(new Map<string, CatalogBlock>())
  const [blocks, setBlocksState] = useState<CatalogBlock[]>([])
  const [blockIds, setBlockIds] = useState<string[]>([])
  const [version, setVersion] = useState(0) // Force re-renders when needed

  // Replace all blocks (for initial load or major updates)
  const setBlocks = useCallback(
    (newBlocks: CatalogBlock[]) => {
      // Sanitize blocks if context is provided
      const blocksToSet = context
        ? sanitizeCatalogBlocks(newBlocks, context)
        : newBlocks

      blocksMap.current.clear()
      blocksToSet.forEach((block) => {
        blocksMap.current.set(block.id, block)
      })
      setBlockIds(blocksToSet.map((b) => b.id))
      setBlocksState(blocksToSet)
      setVersion((v) => v + 1)
    },
    [context],
  )

  // Reorder blocks
  const reorderBlocks = useCallback(
    (newBlockIds: string[]) => {
      setBlockIds(newBlockIds)

      // Calculate blocks with the new order immediately to avoid race condition
      const reorderedBlocks = newBlockIds
        .map((id) => blocksMap.current.get(id)!)
        .filter(Boolean)

      setBlocksState(reorderedBlocks)
      callbacks?.onBlocksReorder?.(reorderedBlocks)
    },
    [callbacks],
  )

  // Get a single block by ID
  const getBlock = useCallback((blockId: string): CatalogBlock | undefined => {
    return blocksMap.current.get(blockId)
  }, [])

  // Update a single block
  const updateBlock = useCallback(
    ({ blockId, payload }: UpdateBlockParams) => {
      const block = blocksMap.current.get(blockId)
      if (block) {
        // Build the updated block by merging payload into data
        const updatedBlock = {
          ...block,
          data: { ...block.data, ...payload },
        } as CatalogBlock

        // If it's a HEADER, replace the entire blocks list via setBlocks
        if (block.type === 'HEADER') {
          const newBlocks = blockIds.map((id) =>
            id === blockId ? updatedBlock : blocksMap.current.get(id)!,
          )
          setBlocks(newBlocks)
        } else {
          // For other types, update the map directly
          blocksMap.current.set(blockId, updatedBlock)
          setBlocksState((previous) =>
            previous.map((candidate) =>
              candidate.id === blockId ? updatedBlock : candidate,
            ),
          )
          // Force re-render for non-header updates
          setVersion((v) => v + 1)
        }
        callbacks?.onBlockChange?.(blockId, block, updatedBlock)
      }
    },
    [blockIds, callbacks, setBlocks, setVersion],
  )

  // Replace a product block's data entirely (used for external refreshes)
  const replaceBlockData = useCallback(
    (blockId: string, newData: AdaptedProduct) => {
      const existing = blocksMap.current.get(blockId)
      if (!existing || existing.type !== 'PRODUCT') return

      // Preserve local-only flags (e.g., detailedSheet) while refreshing from cache
      const updatedBlock: CatalogBlock = {
        ...existing,
        data: {
          ...newData,
          detailedSheet: existing.data?.detailedSheet,
        },
      }
      blocksMap.current.set(blockId, updatedBlock)
      setBlocksState((previous) =>
        previous.map((candidate) =>
          candidate.id === blockId ? updatedBlock : candidate,
        ),
      )
      setVersion((v) => v + 1)
      // Do not call onBlockChange for external refreshes to avoid marking as changed
    },
    [],
  )

  const reconcileBlocks = useCallback(
    (incomingBlocks: CatalogBlock[], options?: ReconcileBlocksOptions) => {
      const preserveBlockIds = options?.preserveBlockIds ?? new Set<string>()
      const nextBlocksRaw = context
        ? sanitizeCatalogBlocks(incomingBlocks, context)
        : incomingBlocks

      const nextMap = new Map<string, CatalogBlock>()
      const currentBlocks = blocksMap.current
      let hasChanged = nextBlocksRaw.length !== blockIds.length

      const nextBlocks = nextBlocksRaw.map((incomingBlock, index) => {
        const existingBlock = currentBlocks.get(incomingBlock.id)

        if (blockIds[index] !== incomingBlock.id) {
          hasChanged = true
        }

        if (!existingBlock || existingBlock.type !== incomingBlock.type) {
          hasChanged = true
          nextMap.set(incomingBlock.id, incomingBlock)
          return incomingBlock
        }

        if (
          incomingBlock.type === 'PRODUCT' &&
          preserveBlockIds.has(incomingBlock.id)
        ) {
          nextMap.set(existingBlock.id, existingBlock)
          return existingBlock
        }

        if (
          incomingBlock.type === 'PRODUCT' &&
          existingBlock.type === 'PRODUCT'
        ) {
          const mergedProductBlock: CatalogBlock = {
            ...incomingBlock,
            data: {
              ...incomingBlock.data,
              detailedSheet: existingBlock.data?.detailedSheet,
            },
          }

          if (
            JSON.stringify(existingBlock) !== JSON.stringify(mergedProductBlock)
          ) {
            hasChanged = true
            nextMap.set(mergedProductBlock.id, mergedProductBlock)
            return mergedProductBlock
          }

          nextMap.set(existingBlock.id, existingBlock)
          return existingBlock
        }

        if (JSON.stringify(existingBlock) !== JSON.stringify(incomingBlock)) {
          hasChanged = true
          nextMap.set(incomingBlock.id, incomingBlock)
          return incomingBlock
        }

        nextMap.set(existingBlock.id, existingBlock)
        return existingBlock
      })

      if (!hasChanged) return

      blocksMap.current = nextMap
      setBlockIds(nextBlocks.map((block) => block.id))
      setBlocksState(nextBlocks)
      setVersion((v) => v + 1)
    },
    [blockIds, context],
  )

  return {
    blocks,
    blockIds,
    version, // Can be used for dependency arrays
    updateBlock,
    reorderBlocks,
    setBlocks,
    reconcileBlocks,
    getBlock,
    replaceBlockData,
  }
}
